import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faLock,
  faMagnifyingGlass,
  faQuoteLeft,
  faReceipt,
  faScaleBalanced,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";

import "../quivioEnclave.scss";
import enclaveLogo from "../img/quivio-enclave/enclave.svg";
import { useTranslation } from "lib/i18n";

const opsStack = [
  "OpenIddict",
  "FastAPI",
  "PostgreSQL",
  "Redis",
  "Qdrant",
  "Seq",
  "Ollama",
  "vLLM",
];

const answerCitations = ["ENG-RUNBOOK-42", "KMS-POLICY-7"];
const pillarIcons = [faShieldHalved, faQuoteLeft, faScaleBalanced];
const traceIcons = [faShieldHalved, faMagnifyingGlass, faCircleCheck];

const getList = (value) => (Array.isArray(value) ? value : []);

const QuivioEnclave = () => {
  const { t } = useTranslation();
  const sectionNav = getList(t("enclave.sectionNav"));
  const proofPoints = getList(t("enclave.proofPoints"));
  const platformPillars = getList(t("enclave.pillars")).map((pillar, index) => ({
    ...pillar,
    icon: pillarIcons[index],
  }));
  const workflowSteps = getList(t("enclave.workflow.steps"));

  return (
    <section className="enclave-landing dark">
      <div className="enclave-landing-texture">
        <div className="landing-grid" />
        <div className="landing-orb landing-orb-a" />
        <div className="landing-orb landing-orb-b" />
        <div className="landing-orb landing-orb-c" />
      </div>

      <main className="enclave-landing-main">
        <section className="enclave-landing-hero">
          <div className="enclave-hero-copy">
            <div className="enclave-product-lockup">
              <img src={enclaveLogo} alt={t("enclave.productAlt")} />
              <div>
                <p>{t("enclave.productName")}</p>
                <span>{t("enclave.productSubtitle")}</span>
              </div>
            </div>

            <nav className="enclave-section-nav" aria-label={t("enclave.sectionNavLabel")}>
              {sectionNav.map(({ href, label }) => (
                <a href={href} key={href}>
                  {label}
                </a>
              ))}
            </nav>

            <div className="enclave-kicker">
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faLock} />
              </span>
              {t("enclave.kicker")}
            </div>

            <h1>{t("enclave.heroHeading")}</h1>
            <p className="enclave-hero-lede">
              {t("enclave.heroLede")}
            </p>

            <div className="enclave-actions">
              <Link to="/contact" className="enclave-action enclave-action-primary">
                {t("enclave.ctaPrimary")}
              </Link>
              <a href="#workflow" className="enclave-action enclave-action-secondary">
                {t("enclave.ctaSecondary")}
              </a>
            </div>

            <div className="enclave-proof-grid">
              {proofPoints.map((item, index) => (
                <div className={`enclave-proof-card ${index === 1 ? "is-offset" : ""}`} key={item}>
                  <strong>0{index + 1}</strong>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <HeroConsole />
        </section>

        <section className="enclave-pillars" id="trust">
          {platformPillars.map((pillar, index) => (
            <article className={`enclave-pillar-card ${index === 1 ? "is-offset" : ""}`} key={pillar.title}>
              <div>
                <span className="enclave-pillar-icon">
                  <FontAwesomeIcon icon={pillar.icon} />
                </span>
                <span className="enclave-pillar-eyebrow">{pillar.eyebrow}</span>
              </div>
              <h2>{pillar.title}</h2>
              <p>{pillar.description}</p>
            </article>
          ))}
        </section>

        <section className="enclave-control-plane" id="workflow">
          <div className="enclave-control-copy">
            <p>{t("enclave.workflow.eyebrow")}</p>
            <h2>{t("enclave.workflow.heading")}</h2>
            <span>
              {t("enclave.workflow.body")}
            </span>
          </div>

          <div className="enclave-workflow-grid">
            {workflowSteps.map(({ step, title, description }) => (
              <div className="enclave-workflow-card" key={step}>
                <strong>{step}</strong>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="enclave-ops" id="ops">
          <div className="enclave-ops-copy">
            <p>{t("enclave.ops.eyebrow")}</p>
            <h2>{t("enclave.ops.heading")}</h2>
            <span>
              {t("enclave.ops.body")}
            </span>
          </div>

          <div className="enclave-stack-panel">
            <div className="enclave-stack-grid">
              {opsStack.map((item, index) => (
                <span
                  className="landing-stack-card"
                  key={item}
                  style={{ animationDelay: `${index * 70}ms` }}
                >
                  {item}
                </span>
              ))}
            </div>
            <p>
              {t("enclave.ops.stackNote")}
            </p>
          </div>
        </section>

        <section className="enclave-final-cta">
          <div className="landing-cta-dots" />
          <div>
            <h2>{t("enclave.finalCta.heading")}</h2>
            <p>
              {t("enclave.finalCta.body")}
            </p>
            <Link to="/contact" className="enclave-action enclave-action-dark">
              {t("enclave.finalCta.button")}
            </Link>
          </div>
        </section>
      </main>
    </section>
  );
};

const HeroConsole = () => {
  const { t } = useTranslation();
  const consoleTrace = getList(t("enclave.console.trace")).map((item, index) => ({
    ...item,
    icon: traceIcons[index],
  }));
  const auditEvents = getList(t("enclave.console.auditEvents"));

  return (
    <div className="enclave-hero-console">
      <div className="landing-shape landing-shape-square" />
      <div className="landing-shape landing-shape-pill" />
      <div className="landing-shape landing-shape-dot" />

      <div className="enclave-console-card">
        <div className="enclave-console-chrome">
          <span />
          <span />
          <span />
          <strong>{t("enclave.console.status")}</strong>
        </div>

        <div className="enclave-console-question">
          <span>{t("enclave.console.questionEyebrow")}</span>
          <p>{t("enclave.console.question")}</p>
        </div>

        <div className="enclave-console-trace">
          {consoleTrace.map(({ title, detail, icon }) => (
            <div className="enclave-console-trace-row" key={title}>
              <span className="enclave-console-trace-icon" aria-hidden="true">
                <FontAwesomeIcon icon={icon} />
              </span>
              <div>
                <strong>{title}</strong>
                <span>{detail}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="enclave-answer-preview">
          <p>{t("enclave.console.answerEyebrow")}</p>
          <span>
            {t("enclave.console.answer")}
          </span>
          <div>
            {answerCitations.map((citation) => (
              <small key={citation}>{citation}</small>
            ))}
          </div>
        </div>
      </div>

      <div className="enclave-audit-card">
        <div className="enclave-audit-heading">
          <div>
            <p>{t("enclave.console.auditEyebrow")}</p>
            <h2>{t("enclave.console.auditHeading")}</h2>
          </div>
          <FontAwesomeIcon icon={faReceipt} aria-hidden="true" />
        </div>
        <div className="enclave-audit-events">
          {auditEvents.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <small>{t("enclave.console.auditReceipt")}</small>
      </div>
    </div>
  );
};

export default QuivioEnclave;
